/**
 * Keyboard-aware layout for the native shell.
 *
 * iOS runs with KeyboardResize.None: the web view never resizes, so the
 * keyboard floats over the real app (its rounded, translucent top edge shows
 * app content behind it, not a native window). Instead, layout follows the
 * keyboard through one animated CSS length, `--kb` (see index.css):
 *
 *  - `html.kb-inset` makes <body> a fixed box ending at `--kb`, and the
 *    containing block for every position:fixed descendant — so modals,
 *    sheets and toasts (class- or inline-styled, portals included) all end at
 *    the keyboard's top edge, like they did when the web view itself shrank.
 *  - Tailwind's `h-dvh` / `min-h-dvh` / `max-h-dvh[-N]` and `viewportHeight()`
 *    all subtract `--kb`, so full-screen layouts shrink in step with it.
 *  - `--kb` is a registered, transitioned property, so all of the above glide
 *    together with the keyboard's own slide instead of snapping.
 *
 * Android keeps the platform's native adjustResize (already in sync with the
 * keyboard); only the shared bits — `.keyboard-open`, bottom pinning — apply.
 */

/** Opt-in marker for scroll containers that should stay pinned to their
 *  bottom (latest message) while the keyboard opens or closes. */
export const KEYBOARD_PIN_ATTR = 'data-keyboard-pin';

/** Slightly longer than the ~250ms keyboard animation, so pinning covers the
 *  whole resize, including the final frame. */
const PIN_DURATION_MS = 400;

function isAtBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= 4;
}

/**
 * Keeps opted-in scrollers that are currently at their bottom pinned there
 * while their height animates, so the latest message isn't slid under the
 * composer as it rises. Scrollers the user had scrolled up are left alone.
 */
export function pinScrollersToBottom(root: Document | Element = document): () => void {
  const pinned = Array.from(root.querySelectorAll<HTMLElement>(`[${KEYBOARD_PIN_ATTR}]`)).filter(isAtBottom);
  if (pinned.length === 0) return () => {};

  let frame = 0;
  const stopAt = performance.now() + PIN_DURATION_MS;
  const tick = (now: number) => {
    for (const el of pinned) el.scrollTop = el.scrollHeight;
    if (now < stopAt) frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(frame);
}

/**
 * Scroll containers above `el`, nearest first, stopping short of the
 * document itself. With the keyboard floating over the app, native
 * Element.scrollIntoView() also scrolls the page/viewport when iOS considers
 * the target obscured by the keyboard — pushing the whole screen up and
 * leaving a gap above the keyboard. These helpers only ever move the app's
 * own scroll containers.
 */
function scrollContainers(el: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if (overflowY === 'auto' || overflowY === 'scroll') out.push(p);
  }
  return out;
}

/** Scrolls the list containing `el` (e.g. a chat's end-of-thread sentinel)
 *  to its bottom — without touching the page. */
export function scrollListToBottom(el: HTMLElement | null, behavior: 'smooth' | 'auto' = 'smooth'): void {
  const list = el && scrollContainers(el)[0];
  list?.scrollTo({ top: list.scrollHeight, behavior });
}

/** Scrolls the list containing `el` so `el` sits in the middle of it, without
 *  touching the page (a search result jumped to, for instance). */
export function centerInList(el: HTMLElement | null, behavior: 'smooth' | 'auto' = 'smooth'): void {
  const list = el && scrollContainers(el)[0];
  if (!el || !list) return;
  const box = list.getBoundingClientRect();
  const target = el.getBoundingClientRect();
  const delta = target.top + target.height / 2 - (box.top + box.height / 2);
  list.scrollBy({ top: delta, behavior });
}

/** Like scrollIntoView({ block: 'nearest' }), but only moves the app's own
 *  scroll containers, never the page. */
function revealWithinContainers(el: HTMLElement): void {
  const target = el.getBoundingClientRect();
  for (const container of scrollContainers(el)) {
    const box = container.getBoundingClientRect();
    const delta = target.bottom > box.bottom ? target.bottom - box.bottom
      : target.top < box.top ? target.top - box.top
      : 0;
    if (delta !== 0) {
      container.scrollBy({ top: delta, behavior: 'smooth' });
      return;
    }
  }
}

/**
 * iOS only (kb-inset): the document never scrolls — everything scrolls inside
 * #root — so any page offset is iOS pushing the screen up to reveal a focused
 * field above the keyboard. Undo it immediately, whatever triggered it.
 */
export function lockDocumentScroll(): () => void {
  const reset = () => {
    if (window.scrollX !== 0 || window.scrollY !== 0 || (window.visualViewport?.offsetTop ?? 0) !== 0) {
      window.scrollTo(0, 0);
    }
  };
  window.addEventListener('scroll', reset, { passive: true });
  window.visualViewport?.addEventListener('scroll', reset);
  return () => {
    window.removeEventListener('scroll', reset);
    window.visualViewport?.removeEventListener('scroll', reset);
  };
}

function isEditable(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly;
  if (el instanceof HTMLInputElement) {
    return !el.readOnly && !['button', 'checkbox', 'radio', 'submit', 'reset', 'file', 'range', 'color', 'hidden', 'image'].includes(el.type);
  }
  return false;
}

/**
 * With KeyboardResize.None the web view no longer reveals the focused field
 * itself (the keyboard plugin detaches it from iOS's keyboard notifications),
 * so once the layout has shrunk, scroll the focused field into view within
 * its own scroll containers. A no-op when it's already visible.
 */
export function revealFocusedField(): void {
  const el = document.activeElement;
  if (!isEditable(el)) return;
  revealWithinContainers(el);
}

/** Applies a keyboard height (px) to the layout; 0 when hidden. */
export function setKeyboardInset(height: number): void {
  document.documentElement.style.setProperty('--kb', `${Math.max(0, Math.round(height))}px`);
}
